/**
 * Ketma-ket modulli (sequential modular) flowsheet yechuvchi.
 *
 * ALGORITM
 *   Bloklar (graph.ts) tartibida: siklsiz blok — bir marta; retsiklli blok —
 *   tear stream'lar taxmin → tugunlar tartibda → tear'ning yangi qiymati g(x);
 *   x_{k+1} = Wegstein(x_k, g_k) (yoki oddiy almashtirish); konvergensiya:
 *     max_i |g_i − x_i| / max(|x_i|, ε_i) < tol,  ε = 1e-6·Σ feed (sarflar), 1 K (T)
 *   Divergensiya: qoldiq NaN yoki boshlang'ichdan 1e6 marta oshsa.
 *
 * NATIJA — xato emas, diagnostika: status, iteratsiyalar, qoldiq tarixi, tear'lar.
 * Umumiy balanslar (massa, energiya) alohida tekshiriladi (`checkBalances`).
 */
import { generateVariant } from '../scenario/variant';
import { substituteRefs } from '../scenario/path';
import { wegsteinInit, wegsteinStep } from '../solvers/wegstein';
import type { Warning, WarningSink } from '../types';
import { planGraph, type Block, type GraphPlan } from './graph';
import { computeNode, type NodeResult } from './nodes';
import type { Flowsheet, FlowsheetNode } from './schema';
import { enthalpy, massFlow, totalFlow, type Stream } from './stream';

export type SolveStatus = 'converged' | 'maxIterations' | 'diverged';

export interface BlockReport {
  nodes: string[];
  tears: string[];
  iterations: number;
  residual: number;
  residualHistory: number[];
  status: SolveStatus;
}

export interface Balances {
  massIn: number;
  massOut: number;
  massRelError: number;
  energyIn: number;
  energyOut: number;
  energyRelError: number;
}

export interface FlowsheetResult {
  status: SolveStatus;
  variant: Record<string, number | string>;
  blocks: BlockReport[];
  streams: Record<string, Stream>;
  nodes: Record<string, Omit<NodeResult, 'outlets'>>;
  balances: Balances;
  warnings: Warning[];
  /** umumiy iteratsiyalar (barcha bloklar) */
  iterations: number;
}

export interface SolveOptions {
  seed?: string;
  /** sxema ustiga qo'yiladigan o'zgarishlar (masalan talaba boshqaruvi): nodeId → maydon → qiymat */
  overrides?: Record<string, Record<string, unknown>>;
  /** Wegstein'ni majburan o'chirish (taqqoslash uchun) */
  wegstein?: boolean;
  /** Variant qiymatlari (seed bo'yicha generatsiya ustiga; lab varianti orqali beriladi) */
  variant?: Record<string, number | string>;
}

/** Chuqur birlashtirish: obyektlar rekursiv, massiv va skalyarlar almashtiriladi */
function deepMerge(base: unknown, patch: unknown): unknown {
  if (patch === null || typeof patch !== 'object' || Array.isArray(patch)) return patch;
  if (Array.isArray(base)) {
    // massiv elementini indeks bo'yicha o'zgartirish: { "0": {...} }
    const arr: unknown[] = [...base];
    for (const [k, v] of Object.entries(patch as Record<string, unknown>)) {
      const i = Number(k);
      if (Number.isInteger(i) && i >= 0) arr[i] = deepMerge(arr[i], v);
    }
    return arr;
  }
  if (base === null || typeof base !== 'object') return { ...(patch as object) };
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(patch as Record<string, unknown>)) out[k] = deepMerge(out[k], v);
  return out;
}

function applyOverrides(f: Flowsheet, overrides: SolveOptions['overrides']): Flowsheet {
  if (!overrides) return f;
  return {
    ...f,
    nodes: f.nodes.map((n) => (overrides[n.id] ? (deepMerge(n, overrides[n.id]) as FlowsheetNode) : n)),
  };
}

function tearVector(streams: Record<string, Stream>, tears: string[], comps: readonly string[]): number[] {
  const v: number[] = [];
  for (const t of tears) {
    const s = streams[t] as Stream;
    for (const c of comps) v.push(s.flow[c] ?? 0);
    v.push(s.T);
  }
  return v;
}

function setTears(streams: Record<string, Stream>, tears: string[], comps: readonly string[], v: number[]): void {
  let k = 0;
  for (const t of tears) {
    const s = streams[t] as Stream;
    for (const c of comps) s.flow[c] = Math.max(0, v[k++] as number);
    s.T = v[k++] as number;
  }
}

export function solveFlowsheet(def: Flowsheet, opts: SolveOptions = {}): FlowsheetResult {
  const seed = opts.seed ?? 'default';
  const variant = { ...generateVariant(def as unknown as Parameters<typeof generateVariant>[0], seed), ...(opts.variant ?? {}) };
  const resolved = applyOverrides(substituteRefs(def, variant) as Flowsheet, opts.overrides);
  const comps = resolved.components;
  const warnings: WarningSink = [];
  const ctx = { components: comps, phase: resolved.phase, Tref: resolved.Tref, warnings };
  const plan: GraphPlan = planGraph(resolved);
  const nodeById = new Map(resolved.nodes.map((n) => [n.id, n]));
  // Tear boshlang'ich bosimi: eng katta feed bosimi (aks holda mixer min-qoidasi 1 atm ni "qulflab" qo'yadi)
  const feedP = resolved.nodes.reduce((m, n) => (n.type === 'feed' && typeof n.P === 'number' ? Math.max(m, n.P) : m), 0) || 101325;
  const streams: Record<string, Stream> = {};
  for (const s of resolved.streams) {
    streams[s.id] = { id: s.id, flow: Object.fromEntries(comps.map((c) => [c, s.guess?.flow[c] ?? 0])), T: s.guess?.T ?? resolved.Tref, P: s.guess?.P ?? feedP };
  }
  const nodeResults: Record<string, NodeResult> = {};
  const feedTotal = resolved.nodes
    .filter((n): n is Extract<FlowsheetNode, { type: 'feed' }> => n.type === 'feed')
    .reduce((a, n) => a + Object.values(n.flow).reduce<number>((x, y) => x + (typeof y === 'number' ? y : 0), 0), 0);
  const epsFlow = Math.max(1e-12, 1e-6 * feedTotal);

  const runNodes = (ids: string[]) => {
    for (const id of ids) {
      const node = nodeById.get(id) as FlowsheetNode;
      const inlets = (plan.inlets[id] ?? []).map((s) => streams[s.id] as Stream);
      const outletIds = (plan.outlets[id] ?? []).map((s) => s.id);
      const r = computeNode(node, inlets, outletIds, ctx);
      for (const [sid, st] of Object.entries(r.outlets)) {
        if (!streams[sid]) throw new Error(`${id}: produced unknown stream ${sid}`);
        streams[sid] = st;
      }
      nodeResults[id] = r;
    }
  };

  const blocks: BlockReport[] = [];
  let status: SolveStatus = 'converged';
  let totalIter = 0;
  const useWeg = opts.wegstein ?? resolved.solver.wegstein;
  for (const b of plan.blocks as Block[]) {
    if (b.tears.length === 0) {
      runNodes(b.nodes);
      blocks.push({ nodes: b.nodes, tears: [], iterations: 1, residual: 0, residualHistory: [], status: 'converged' });
      continue;
    }
    const weg = wegsteinInit();
    const history: number[] = [];
    let bStatus: SolveStatus = 'maxIterations';
    let iter = 0;
    let x = tearVector(streams, b.tears, comps);
    for (iter = 1; iter <= resolved.solver.maxIter; iter++) {
      setTears(streams, b.tears, comps, x);
      runNodes(b.nodes);
      const g = tearVector(streams, b.tears, comps);
      // qoldiq
      let res = 0;
      let k = 0;
      for (let t = 0; t < b.tears.length; t++) {
        for (let c = 0; c < comps.length; c++, k++) res = Math.max(res, Math.abs((g[k] as number) - (x[k] as number)) / Math.max(Math.abs(x[k] as number), epsFlow));
        res = Math.max(res, Math.abs((g[k] as number) - (x[k] as number)) / Math.max(Math.abs(x[k] as number), 1));
        k++;
      }
      history.push(res);
      if (!Number.isFinite(res) || (history.length > 3 && res > 1e6 * (history[0] as number))) {
        bStatus = 'diverged';
        break;
      }
      if (res < resolved.solver.tol) {
        bStatus = 'converged';
        x = g;
        setTears(streams, b.tears, comps, x);
        break;
      }
      x = useWeg ? wegsteinStep(weg, x, g, { qMin: resolved.solver.qMin, qMax: resolved.solver.qMax }) : g;
    }
    if (bStatus === 'converged') runNodes(b.nodes); // yakuniy izchil holat
    totalIter += Math.min(iter, resolved.solver.maxIter);
    blocks.push({ nodes: b.nodes, tears: b.tears, iterations: Math.min(iter, resolved.solver.maxIter), residual: history[history.length - 1] ?? 0, residualHistory: history, status: bStatus });
    if (bStatus !== 'converged') {
      status = bStatus;
      warnings.push({ code: bStatus === 'diverged' ? 'flowsheet.diverged' : 'flowsheet.notConverged', severity: 'critical', params: { block: b.nodes.join('→'), iterations: iter } });
    }
  }

  const nodes: FlowsheetResult['nodes'] = {};
  for (const [id, r] of Object.entries(nodeResults)) {
    const { outlets: _o, ...rest } = r;
    nodes[id] = rest;
  }
  const balances = checkBalances(resolved, streams, nodeResults, plan);
  // property.outOfRange — modda+xossa bo'yicha bitta (har T uchun takrorlanmasin)
  const seen = new Set<string>();
  const compact = warnings.filter((w) => {
    if (w.code !== 'property.outOfRange') return true;
    const k = `${w.params?.substance}:${w.params?.property}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  warnings.length = 0;
  warnings.push(...compact);
  return { status, variant, blocks, streams, nodes, balances, warnings, iterations: totalIter };
}

/**
 * Umumiy balanslar: massa (feed → product), energiya
 *   Σ H_feed + Σ Q_node + Σ (−ξ ΔH_r) = Σ H_product   (sezgir entalpiya bazasi, Tref)
 */
export function checkBalances(f: Flowsheet, streams: Record<string, Stream>, results: Record<string, NodeResult>, plan: GraphPlan): Balances {
  let massIn = 0;
  let massOut = 0;
  let energyIn = 0;
  let energyOut = 0;
  for (const n of f.nodes) {
    if (n.type === 'feed') {
      for (const s of plan.outlets[n.id] ?? []) {
        const st = streams[s.id] as Stream;
        massIn += massFlow(st);
        energyIn += enthalpy(st, f.phase, f.Tref);
      }
    } else if (n.type === 'product') {
      for (const s of plan.inlets[n.id] ?? []) {
        const st = streams[s.id] as Stream;
        massOut += massFlow(st);
        energyOut += enthalpy(st, f.phase, f.Tref);
      }
    }
    const r = results[n.id];
    if (r) energyIn += r.Q + (r.reactionHeat ?? 0);
  }
  const eScale = Math.max(Math.abs(energyIn), Math.abs(energyOut), 1);
  return {
    massIn,
    massOut,
    massRelError: Math.abs(massIn - massOut) / Math.max(massIn, 1e-12),
    energyIn,
    energyOut,
    energyRelError: Math.abs(energyIn - energyOut) / eScale,
  };
}

/** Oqimlar jadvali uchun qulay ko'rinish */
export function summarizeStream(s: Stream): { id: string; total: number; mass: number; T: number; P: number; x: Record<string, number> } {
  const total = totalFlow(s);
  const x: Record<string, number> = {};
  for (const [c, n] of Object.entries(s.flow)) x[c] = total > 0 ? n / total : 0;
  return { id: s.id, total, mass: massFlow(s), T: s.T, P: s.P, x };
}
